import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HelpCircle, X, Search, ChevronDown, Rocket, Landmark, ClipboardList, Send,
  Receipt, LifeBuoy, Mail, Settings2, BookOpen
} from 'lucide-react';

export interface FaqItem {
  id: string;
  categoria: string;
  pergunta: string;
  resposta: string;
  ordem: number;
  ativo: boolean;
}

export const FAQ_EMAIL_SUPORTE = 'gcf-emendasfederais@saude.sp.gov.br';

// Ícone por categoria (por palavra-chave, para funcionar com categorias criadas pelo admin)
export const iconeDaCategoria = (categoria: string) => {
  const c = categoria.toLowerCase();
  if (c.includes('primeir') || c.includes('começ')) return Rocket;
  if (c.includes('emenda')) return Landmark;
  if (c.includes('plano')) return ClipboardList;
  if (c.includes('envio') || c.includes('assinatura')) return Send;
  if (c.includes('presta') || c.includes('conta')) return Receipt;
  if (c.includes('suporte') || c.includes('acesso')) return LifeBuoy;
  return BookOpen;
};

const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// **negrito** e e-mails clicáveis, sem HTML cru
const renderInline = (texto: string): React.ReactNode[] => {
  const partes = texto.split(/(\*\*[^*]+\*\*|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g);
  return partes.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      return <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>;
    }
    if (/^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/.test(p)) {
      return <a key={i} href={`mailto:${p}`} className="font-medium text-[#0B3B8C] underline underline-offset-2 break-words hover:text-[#08162D]">{p}</a>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

// Texto simples → parágrafos, listas com marcador ("- ") e passo a passo ("1. ")
export const FaqResposta: React.FC<{ texto: string }> = ({ texto }) => {
  const blocos: React.ReactNode[] = [];
  let lista: { tipo: 'ul' | 'ol'; itens: string[] } | null = null;
  let paragrafo: string[] = [];

  const fecharParagrafo = () => {
    if (paragrafo.length) {
      blocos.push(<p key={blocos.length} className="leading-relaxed">{renderInline(paragrafo.join(' '))}</p>);
      paragrafo = [];
    }
  };
  const fecharLista = () => {
    if (!lista) return;
    const l = lista;
    if (l.tipo === 'ol') {
      blocos.push(
        <ol key={blocos.length} className="space-y-2.5">
          {l.itens.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 mt-px rounded-full bg-[#08162D] text-white text-xs font-bold flex items-center justify-center" aria-hidden="true">{i + 1}</span>
              <span className="leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ol>
      );
    } else {
      blocos.push(
        <ul key={blocos.length} className="space-y-1.5">
          {l.itens.map((item, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="flex-shrink-0 w-1.5 h-1.5 mt-[0.6rem] rounded-full bg-[#D92B2B]" aria-hidden="true" />
              <span className="leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
    }
    lista = null;
  };

  for (const linhaBruta of texto.replace(/\r/g, '').split('\n')) {
    const linha = linhaBruta.trim();
    const ul = linha.match(/^[-•]\s+(.*)$/);
    const ol = linha.match(/^\d+[.)]\s+(.*)$/);
    if (!linha) { fecharParagrafo(); fecharLista(); continue; }
    if (ul || ol) {
      fecharParagrafo();
      const tipo = ul ? 'ul' : 'ol';
      if (!lista || lista.tipo !== tipo) { fecharLista(); lista = { tipo, itens: [] }; }
      lista.itens.push((ul || ol)![1]);
      continue;
    }
    fecharLista();
    paragrafo.push(linha);
  }
  fecharParagrafo();
  fecharLista();

  return <div className="space-y-3 text-[15px] text-slate-700">{blocos}</div>;
};

const Destaque: React.FC<{ texto: string; termo: string }> = ({ texto, termo }) => {
  const t = normalizar(termo.trim());
  if (!t) return <>{texto}</>;
  const idx = normalizar(texto).indexOf(t);
  if (idx < 0) return <>{texto}</>;
  return (
    <>
      {texto.slice(0, idx)}
      <mark className="bg-amber-200 text-inherit rounded px-0.5">{texto.slice(idx, idx + t.length)}</mark>
      {texto.slice(idx + t.length)}
    </>
  );
};

interface FaqPanelProps {
  itens: FaqItem[];
  nomeUsuario?: string;
  boasVindas?: boolean;   // aberto automaticamente ao entrar
  isAdmin?: boolean;
  painelAtivo?: boolean;
  onGerenciar?: () => void;
  onClose: () => void;
}

export const FaqPanel: React.FC<FaqPanelProps> = ({ itens, nomeUsuario, boasVindas, isAdmin, painelAtivo, onGerenciar, onClose }) => {
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string>('todas');
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const visiveis = useMemo(
    () => itens.filter(i => i.ativo).sort((a, b) => a.ordem - b.ordem),
    [itens]
  );

  const categorias = useMemo(() => {
    const vistas: string[] = [];
    for (const i of visiveis) if (!vistas.includes(i.categoria)) vistas.push(i.categoria);
    return vistas;
  }, [visiveis]);

  const filtrados = useMemo(() => {
    const q = normalizar(busca.trim());
    return visiveis.filter(i => {
      if (categoria !== 'todas' && i.categoria !== categoria) return false;
      if (!q) return true;
      return normalizar(i.pergunta).includes(q) || normalizar(i.resposta).includes(q);
    });
  }, [visiveis, busca, categoria]);

  const grupos = useMemo(() => {
    const mapa = new Map<string, FaqItem[]>();
    for (const i of filtrados) {
      if (!mapa.has(i.categoria)) mapa.set(i.categoria, []);
      mapa.get(i.categoria)!.push(i);
    }
    return Array.from(mapa.entries());
  }, [filtrados]);

  // Com busca ativa, abre os resultados para leitura direta
  useEffect(() => {
    if (busca.trim() && filtrados.length <= 4) setAbertos(new Set(filtrados.map(i => i.id)));
  }, [busca, filtrados]);

  // Foco inicial, Esc para fechar, Tab preso no diálogo, rolagem da página travada
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focaveis = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input, a[href], [tabindex="0"]');
      if (!focaveis.length) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowAnterior;
      anterior?.focus?.();
    };
  }, []);

  const alternar = (id: string) => {
    setAbertos(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
  };

  const primeiroNome = (nomeUsuario || '').trim().split(/\s+/)[0];
  const sugestoes = ['PDF', 'CNES', 'justificativa', 'documentos'];

  return (
    <div className="fixed inset-0 z-[115] bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="faq-titulo"
        tabIndex={-1}
        className="bg-white w-full sm:max-w-3xl h-[94vh] sm:h-auto sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden outline-none modal-content"
      >
        {/* Cabeçalho */}
        <div className="bg-[#08162D] px-5 sm:px-7 pt-5 pb-5 border-b-2 border-[#D92B2B] flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-[#D92B2B] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 id="faq-titulo" className="text-xl font-bold text-white leading-tight">Central de Dúvidas</h2>
                <p className="text-sm text-[#B8C1D1] mt-1">Emendas, plano de trabalho e prestação de contas</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-11 h-11 -mr-2 -mt-1 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10"
              aria-label="Fechar central de dúvidas"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {boasVindas && (
            <p className="mt-4 text-sm text-white/90 leading-relaxed">
              {primeiroNome ? <>Olá, <strong className="text-white">{primeiroNome}</strong>! </> : 'Olá! '}
              Antes de começar, veja as respostas para as dúvidas mais comuns. Esta central pode ser aberta de novo a qualquer momento pelo ícone
              <HelpCircle className="inline w-4 h-4 mx-1 -mt-0.5" aria-label="interrogação" /> no topo da tela.
            </p>
          )}

          {/* Busca */}
          <div className="relative mt-4">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" style={{ width: 18, height: 18 }} aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Digite sua dúvida. Ex.: como envio o PDF?"
              aria-label="Buscar nas dúvidas"
              className="w-full h-12 pl-11 pr-11 rounded-xl bg-white text-slate-900 placeholder-slate-500 text-[15px] border-2 border-transparent focus:border-[#D92B2B] focus:outline-none"
            />
            {busca && (
              <button
                onClick={() => setBusca('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                aria-label="Limpar busca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Categorias */}
        {categorias.length > 1 && (
          <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-5 sm:px-7 py-3 overflow-x-auto">
            <div className="flex gap-2 w-max" role="group" aria-label="Filtrar por tema">
              {['todas', ...categorias].map(cat => {
                const ativo = categoria === cat;
                const Icone = cat === 'todas' ? BookOpen : iconeDaCategoria(cat);
                const qtd = cat === 'todas' ? visiveis.length : visiveis.filter(i => i.categoria === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoria(cat)}
                    aria-pressed={ativo}
                    className={`h-9 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap flex items-center gap-1.5 border ${
                      ativo
                        ? 'bg-[#08162D] text-white border-[#08162D]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <Icone className="w-3.5 h-3.5" aria-hidden="true" />
                    {cat === 'todas' ? 'Todas' : cat}
                    <span className={`text-[11px] font-bold ${ativo ? 'text-white/70' : 'text-slate-400'}`}>{qtd}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Lista */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5" aria-live="polite">
          {visiveis.length === 0 ? (
            <div className="text-center py-14">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300" aria-hidden="true" />
              <p className="mt-4 font-semibold text-slate-700">Nenhuma dúvida cadastrada ainda.</p>
              {isAdmin && <p className="text-sm text-slate-500 mt-1">Use “Gerenciar dúvidas” para cadastrar as primeiras perguntas.</p>}
            </div>
          ) : filtrados.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-10 h-10 mx-auto text-slate-300" aria-hidden="true" />
              <p className="mt-4 font-semibold text-slate-800">Nenhuma resposta encontrada para “{busca}”.</p>
              <p className="text-sm text-slate-600 mt-2">Tente outra palavra:</p>
              <div className="flex flex-wrap justify-center gap-2 mt-3">
                {sugestoes.map(s => (
                  <button key={s} onClick={() => { setBusca(s); setCategoria('todas'); }}
                    className="h-9 px-3.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:border-slate-500 bg-white">
                    {s}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-600 mt-6">
                Não encontrou? Escreva para {renderInline(FAQ_EMAIL_SUPORTE)}
              </p>
            </div>
          ) : (
            <div className="space-y-7">
              {grupos.map(([cat, lista]) => {
                const Icone = iconeDaCategoria(cat);
                return (
                  <section key={cat} aria-labelledby={`faq-cat-${cat}`}>
                    <h3 id={`faq-cat-${cat}`} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                      <Icone className="w-4 h-4 text-[#D92B2B]" aria-hidden="true" />
                      {cat}
                    </h3>
                    <div className="space-y-2">
                      {lista.map(item => {
                        const aberto = abertos.has(item.id);
                        return (
                          <div key={item.id} className={`rounded-2xl border transition-colors ${aberto ? 'border-slate-300 bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                            <button
                              onClick={() => alternar(item.id)}
                              aria-expanded={aberto}
                              aria-controls={`faq-resp-${item.id}`}
                              className="w-full min-h-[56px] px-4 sm:px-5 py-3.5 flex items-center justify-between gap-4 text-left rounded-2xl"
                            >
                              <span className="text-[15px] font-semibold text-slate-900 leading-snug">
                                <Destaque texto={item.pergunta} termo={busca} />
                              </span>
                              <ChevronDown className={`w-5 h-5 flex-shrink-0 text-slate-400 transition-transform duration-200 ${aberto ? 'rotate-180 text-[#D92B2B]' : ''}`} aria-hidden="true" />
                            </button>
                            {aberto && (
                              <div id={`faq-resp-${item.id}`} className="px-4 sm:px-5 pb-5 -mt-1 faq-resposta">
                                <div className="border-l-2 border-[#D92B2B]/40 pl-4">
                                  <FaqResposta texto={item.resposta} />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex-shrink-0 border-t border-slate-200 px-5 sm:px-7 py-4 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3 bg-white">
          <a href={`mailto:${FAQ_EMAIL_SUPORTE}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 min-h-[44px]">
            <Mail className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>Ainda com dúvida? <span className="font-semibold underline underline-offset-2">Fale com a equipe</span></span>
          </a>
          <div className="flex gap-2">
            {isAdmin && onGerenciar && (
              <button
                onClick={onGerenciar}
                className="flex-1 sm:flex-none h-11 px-4 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 flex items-center justify-center gap-2"
              >
                <Settings2 className="w-4 h-4" aria-hidden="true" />
                Gerenciar dúvidas
                {painelAtivo === false && <span className="text-[11px] font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">desligado</span>}
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none h-11 px-6 rounded-xl bg-[#D92B2B] hover:bg-[#B71F1F] text-white text-sm font-bold"
            >
              {boasVindas ? 'Entendi, continuar' : 'Fechar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
